import { isAdminRole, KYC_ONBOARDING_ROUTE, kycStatusValue } from './kycGuard'

/**
 * PWA mirror of the backend R7 `VerificationEnforcementMiddleware` gate.
 *
 * The backend rejects authenticated, non-allowlisted requests from
 * unverified users with 403 + envelope `{ error, status, code, traceId }`
 * where `code` is one of the two constants below. This module is the
 * client-side counterpart: it maps those codes (and the session user the
 * route guards read) to the verification flow that unblocks the user.
 *
 * Account rules mirror `GetBlockCode` exactly:
 * - Admin / SuperAdmin are exempt (staff).
 * - Client → phone OTP (`PhoneVerified`); KYC state is irrelevant.
 * - Creator / Agency → verified KYC only (`Pending` is still blocked —
 *   stricter than `KycGate`, which lets pending submissions through to
 *   creation flows).
 * - Unknown account types fall through to the KYC flow (strictest).
 */

export const PHONE_VERIFICATION_REQUIRED = 'PhoneVerificationRequired' as const
export const KYC_VERIFICATION_REQUIRED = 'KycVerificationRequired' as const

/** Reuses the registration OTP screen, which supports a session mode for signed-in users. */
export const PHONE_VERIFICATION_ROUTE = '/otp' as const
export const KYC_VERIFICATION_ROUTE = KYC_ONBOARDING_ROUTE

export type VerificationRoute = typeof PHONE_VERIFICATION_ROUTE | typeof KYC_VERIFICATION_ROUTE

/**
 * Minimal user shape for verification decisions. `useAuth`'s `User`
 * satisfies it — the same state `KycGate`/`GuestOnlyRoute` read.
 */
export interface VerificationUser {
  role: string
  accountType: string
  kycStatus: string
  phoneVerified: boolean
}

/** Maps a backend 403 `code` to the flow that clears it; null for anything else. */
export function routeForVerificationCode(code: unknown): VerificationRoute | null {
  if (code === PHONE_VERIFICATION_REQUIRED) return PHONE_VERIFICATION_ROUTE
  if (code === KYC_VERIFICATION_REQUIRED) return KYC_VERIFICATION_ROUTE
  return null
}

/**
 * Where this user must go to become verified, or null when verified/exempt.
 * Null users (signed out / session still loading) yield null — `ProtectedRoute`
 * owns the unauthenticated redirect, not this helper.
 */
export function verificationRedirectForUser(
  user: VerificationUser | null | undefined,
): VerificationRoute | null {
  if (!user) return null
  if (isAdminRole(user.role ?? '')) return null
  const accountType = (user.accountType ?? '').toLowerCase()
  if (accountType === 'client') {
    return user.phoneVerified ? null : PHONE_VERIFICATION_ROUTE
  }
  if (accountType === 'creator' || accountType === 'agency') {
    return kycStatusValue(user.kycStatus) === 2 ? null : KYC_VERIFICATION_ROUTE
  }
  return KYC_VERIFICATION_ROUTE
}

/** Hub handshakes fail for unverified users, so sockets must not start for them. */
export function canStartHubs(user: VerificationUser | null | undefined): boolean {
  return user != null && verificationRedirectForUser(user) === null
}

/** True for the screens that complete verification (never redirect away from them). */
export function isVerificationRoute(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  return path === PHONE_VERIFICATION_ROUTE || path === KYC_VERIFICATION_ROUTE
}

const AUTH_ROUTES = new Set(['/login', '/register', '/otp', '/kyc-onboarding', '/onboarding'])

/**
 * Validates a post-verification return target: same-origin app paths only,
 * never back into an auth/verification screen (that would loop). Returns
 * the path with its query string, or null.
 */
export function safeNext(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null
  const path = value.split('?')[0]?.replace(/\/+$/, '') || '/'
  if (path === '/' || AUTH_ROUTES.has(path)) return null
  return value
}

/** Reads the `?next=` return target the 403 interceptor stores; null when absent/unsafe. */
export function readReturnTarget(search?: string): string | null {
  const params = new URLSearchParams(
    search ?? (typeof window !== 'undefined' ? window.location.search : ''),
  )
  return safeNext(params.get('next'))
}

/**
 * Verification URL carrying the return target, so the user lands where they
 * were going after verifying. Falls back to the bare route when the return
 * target is missing or unsafe.
 */
export function verificationUrl(route: VerificationRoute, returnTo: unknown): string {
  const next = safeNext(returnTo)
  return next ? `${route}?next=${encodeURIComponent(next)}` : route
}

/** Extracts the backend error `code` (e.g. from an axios 403) without importing axios. */
export function errorCodeOf(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null
  const data = (error as { response?: { data?: unknown } }).response?.data
  if (!data || typeof data !== 'object') return null
  const code = (data as { code?: unknown }).code
  return typeof code === 'string' ? code : null
}
